/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { isValidEmail } from './validations';

/** Top 5 most frequent email domains */
export const EMAIL_DOMAIN_LIST = [
  '@gmail.com',
  '@yahoo.com',
  '@outlook.com',
  '@hotmail.com',
  '@icloud.com',
] as const;

const EMAIL_DOMAIN_DROPDOWN_ITEM_HEIGHT = 44;
/** Taller min height so a single suggestion isn't clipped by dropdown padding. */
const EMAIL_DOMAIN_DROPDOWN_SINGLE_ITEM_HEIGHT = 56;
const EMAIL_DOMAIN_DROPDOWN_MAX_ITEMS = 5;

/**
 * Builds up to 5 email domain suggestions for the current input.
 */
export const getEmailDomainSuggestions = (value: string): string[] => {
  if (isValidEmail(value.trim())) {
    return [];
  }

  const atIndex = value.indexOf('@');

  if (atIndex === -1) {
    return EMAIL_DOMAIN_LIST.map((domain) => `${value}${domain}`);
  }

  const localPart = value.slice(0, atIndex);
  const domainQuery = value.slice(atIndex).toLowerCase();

  return EMAIL_DOMAIN_LIST.filter((domain) =>
    domain.toLowerCase().startsWith(domainQuery),
  ).map((domain) => `${localPart}${domain}`);
};

export const getEmailDomainDropdownHeight = (itemCount: number): number => {
  const count = Math.min(
    Math.max(itemCount, 0),
    EMAIL_DOMAIN_DROPDOWN_MAX_ITEMS,
  );
  if (count <= 0) {
    return 0;
  }
  if (count === 1) {
    return EMAIL_DOMAIN_DROPDOWN_SINGLE_ITEM_HEIGHT;
  }
  return count * EMAIL_DOMAIN_DROPDOWN_ITEM_HEIGHT;
};

/**
 * Returns the typed prefix to highlight inside a suggestion, if present.
 */
export const getTypedEmailPrefixForHighlight = (
  value: string,
  suggestion: string,
): string | undefined => {
  if (!value) {
    return undefined;
  }
  if (suggestion.startsWith(value)) {
    return value;
  }
  if (suggestion.toLowerCase().startsWith(value.toLowerCase())) {
    return suggestion.slice(0, value.length);
  }
  return undefined;
};

/**
 * Paste of a complete email should not show the dropdown.
 */
export const isCompleteEmailPaste = (
  previousValue: string,
  nextValue: string,
): boolean => {
  const insertedLength = nextValue.length - previousValue.length;
  return insertedLength > 1 && isValidEmail(nextValue.trim());
};
